import { memo, useRef, useReducer, useEffect, useCallback, useMemo } from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import API from '../components/api';
import {
  CATEGORIES_OPTIONS,
  PHASES_OPTIONS,
  STATUS_OPTIONS,
  DEVICES_OPTIONS,
} from '../components/constants';
import {
  isReseller,
  hasSmartMeters,
  hasVoltawareSensors,
} from '../components/utils';

import ErrorMessage from '../elements/error-message';
import Input from '../elements/forms/input';
import Loading from '../elements/loading';
import Select from '../elements/forms/select';
import Text from '../elements/text';
import Title from '../elements/title';
import Button from '../elements/button';

import './summary.sass';

const COLORS = ['#00c49f', '#ffbb28', '#0088fe'];

const SummaryChart = memo(function SummaryChart({ data, isMobile }) {
  if (!data || data.length === 0) {
    return null;
  }

  if (isMobile) {
    return (
      <ResponsiveContainer height={400} width="100%">
        <PieChart>
          <Legend
            align="center"
            iconSize={30}
            layout="vertical"
            verticalAlign="bottom"
          />
          <Pie data={data} dataKey="value" label>
            {data.map((entry, index) => (
              <Cell
                fill={COLORS[index % COLORS.length]}
                key={index}
                name={`${entry.name}`}
              />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <PieChart height={400} width={800}>
      <Legend
        align="right"
        iconSize={30}
        layout="vertical"
        verticalAlign="middle"
      />
      <Pie data={data} dataKey="value" label>
        {data.map((entry, index) => (
          <Cell
            fill={COLORS[index % COLORS.length]}
            key={index}
            name={`${entry.name}`}
          />
        ))}
      </Pie>
      <Tooltip />
    </PieChart>
  );
});

function Summary() {
  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      category: '',
      chartData: [],
      chartNoData: true,
      chartRawData: [],
      countriesOptions: [],
      country: '',
      hasSearched: false,
      lastSearch: '',
      loading: false,
      loadingCountries: true,
      loadingReseller: false,
      phases: '',
      resellerId: '',
      resellerName: '',
      resellers: [],
      status: '',
      device:
        hasSmartMeters() && hasVoltawareSensors()
          ? DEVICES_OPTIONS[0].value
          : null,
    }
  );

  const isMountedRef = useRef(null);
  const lastSearchKeyRef = useRef(null);
  const resultsCacheRef = useRef(new Map());

  const currentSearchKey = useMemo(
    () =>
      JSON.stringify({
        category: state.category,
        country: state.country,
        phases: state.phases,
        resellerId: state.resellerId,
        status: state.status === 'ALL' ? '' : state.status,
        device: state.device,
      }),
    [
      state.category,
      state.country,
      state.phases,
      state.resellerId,
      state.status,
      state.device,
    ]
  );

  const resize = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }

    const currentIsMobile = window.innerWidth <= 768;
    if (currentIsMobile !== state.isMobile) {
      setState({ isMobile: currentIsMobile });
    }
  }, [state.isMobile]);

  const loadCountries = useCallback(() => {
    const fn = isReseller() ? API.COUNTRY.GENERAL : API.COUNTRY.SUMMARY;

    fn()
      .then((response) => {
        if (!isMountedRef.current) {
          return;
        }

        if (response.status === 200) {
          setState({
            countriesOptions: response.data.map((country) => ({
              label: country.name,
              value: country.isoCode,
            })),
            loadingCountries: false,
          });
        } else {
          setState({ loadingCountries: false });
          console.error('Error while loading countries.');
        }
      })
      .catch(() => {
        setState({ loadingCountries: false });
        console.error('Error while loading countries.');
      });
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadCountries();
    window.addEventListener('resize', resize);
    resize();

    return () => {
      window.removeEventListener('resize', resize);
      isMountedRef.current = false;
    };
  }, [loadCountries, resize]);

  const change = useCallback((e) => {
    const target = e.target.name;
    setState({
      [target]: e.target.value,
    });
  }, []);

  const changeReseller = useCallback((e) => {
    setState({ resellerName: e.target.value });
  }, []);

  useEffect(() => {
    if (state.resellerName === '') {
      setState({ resellerId: '', resellers: [] });
      return;
    }

    if (
      state.resellerName.length <= 1 ||
      state.resellerName.length >= 30
    ) {
      setState({ resellers: [] });
      return;
    }

    const handler = setTimeout(() => {
      API.RESELLERS.SEARCH(state.resellerName, 'NAME').then((response) => {
        const resellers = response.data.map((item) => {
          return { id: item.id, name: item.name };
        });
        setState({ resellers: resellers });
      });
    }, 400);

    return () => clearTimeout(handler);
  }, [state.resellerName]);

  const selectReseller = useCallback((e) => {
    e.preventDefault();
    setState({
      resellerName: e.target.name,
      resellerId: e.target.id,
      resellers: [],
    });
  }, []);

  const search = useCallback(() => {
    const cached = resultsCacheRef.current.get(currentSearchKey);
    if (cached) {
      lastSearchKeyRef.current = currentSearchKey;
      setState({
        ...cached,
        hasSearched: true,
        loading: false,
        lastSearch: state.resellerId,
      });
      return;
    }

    lastSearchKeyRef.current = currentSearchKey;
    setState({ loading: true });

    API.STOCK.GENERAL_STATUS(
      state.category,
      state.country,
      state.phases,
      state.resellerId,
      state.status === 'ALL' ? '' : state.status,
      state.device
    )
      .then((response) => {
        if (!isMountedRef.current) {
          return;
        }

        let data = [];
        let noData = true;

        if (state.device === 'LOW_RESOLUTION_METER') {
          const totalInstalled = response.data.reduce((acc, curr) => {
            const safeCount = curr.count > 0 ? curr.count : 0;
            if (safeCount > 0) {
              noData = false;
            }
            return acc + safeCount;
          }, 0);

          data = [
            {
              name: 'INSTALLED',
              value: totalInstalled,
            },
          ];
        } else {
          data = response.data.map((obj) => {
            const safeCount = obj.count > 0 ? obj.count : 0;
            if (safeCount > 0) {
              noData = false;
            }
            return { name: obj.sisStatus, value: safeCount };
          });
        }

        const nextState = {
          chartData: data,
          chartNoData: noData,
          chartRawData: data,
        };

        resultsCacheRef.current.set(currentSearchKey, nextState);

        setState({
          ...nextState,
          hasSearched: true,
          lastSearch: state.resellerId,
          loading: false,
        });
      })
      .catch((error) => {
        if (!isMountedRef.current) {
          return;
        }
        console.error(error);
        setState({
          hasSearched: true,
          loading: false,
        });
      });
  }, [
    currentSearchKey,
    state.category,
    state.country,
    state.device,
    state.phases,
    state.resellerId,
    state.status,
  ]);

  return (
    <section id="page-summary">
      <Title element="h4" text="Summary" />

      <Text
        bold={true}
        text={`You can filter the results to see specific information by selecting one or several filters below.`}
      />

      <section className={isReseller() ? 'col-4' : ''}>
        {hasSmartMeters() && hasVoltawareSensors() ? (
          <div>
            <Select
              change={change}
              disabled={state.loading}
              noEmptyOption={true}
              name="device"
              options={DEVICES_OPTIONS}
              placeholder="Device"
              value={state.device}
            />
          </div>
        ) : null}

        {!isReseller() ? (
          <div>
            <Input
              autocomplete="off"
              change={changeReseller}
              data={state.resellers}
              disabled={state.loading}
              name="resellerName"
              placeholder="Reseller"
              select={selectReseller}
              type="autoComplete"
              value={state.resellerName}
            />
          </div>
        ) : null}

        <div>
          <Select
            change={change}
            disabled={state.loading}
            name="status"
            options={STATUS_OPTIONS}
            placeholder="Status"
            value={state.status}
          />
        </div>
        <div>
          <Select
            change={change}
            disabled={state.loading}
            name="country"
            options={state.countriesOptions}
            placeholder="Country"
            value={state.country}
          />
        </div>
        <div>
          <Select
            change={change}
            disabled={state.loading}
            name="phases"
            options={PHASES_OPTIONS}
            placeholder="Number of phases"
            value={state.phases}
          />
        </div>

        <div>
          <Select
            change={change}
            disabled={state.loading}
            name="category"
            options={CATEGORIES_OPTIONS}
            placeholder="Category"
            value={state.category}
          />
        </div>

        <div>
          <Button
            action="search"
            click={search}
            disabled={state.loading}
            aria-label="search"
          />
        </div>
      </section>

      {state.loading ? (
        <Loading />
      ) : state.hasSearched && state.chartNoData ? (
        <ErrorMessage
          align="center"
          message="No sensor matches your search. Please verify your filters."
        />
      ) : state.hasSearched ? (
        <div className="chart-wrapper">
          <SummaryChart data={state.chartData} isMobile={state.isMobile} />
        </div>
      ) : null}
    </section>
  );
}

export default Summary;
